// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from '../../../../../node_module_clones/recompose';
import DocumentTitle from 'react-document-title';
import { get } from 'lodash/fp';

// Utils
import { analyticsEvent } from '../../../../analytics';
import getClientConfig from '../../../../utils/getClientConfig';
import { hasChanged } from '../../../../utils/react/propsComparisons';

// Selectors
import notificationsUtilSelector from './notificationsUtilSelector';

// Components
import markSeenNotificationsDecorator from './markSeenNotificationsDecorator';
import notificationsFetchDecorator from './notificationsFetchDecorator';
import notificationsObservedDecorator from './notificationsObservedDecorator';
import TooltipSource from '../../../../components/tooltips/TooltipSource';
import Icon from '../../../../components/icons/Icon';
import PopupTrigger from '../../../../components/popupPanel/PopupTrigger';
import CountIndicator from '../../../../components/misc/CountIndicator';
import NotificationsPopup from './popup/NotificationsPopup';
import PushNotificationsSuggestionPopup from './pushPopup/PushNotificationsSuggestionPopupContainer';

// Constants
import { PopupIds, PopupSections } from '../../../../components/popupPanel/popupConstants';

// Styles
import '../HeaderUtil.scss';

const config = getClientConfig();

const FAVICON = get('faviconUrl', config) || '/img/milanote-logo.ico';
const FAVICON_ON = FAVICON.replace('.ico', '-on.ico');

const newNotificationsCountHasChanged = hasChanged('newNotificationsCount');

const dispatchNotificationsAnalyticsEvent = (isOpen) => (isOpen ? analyticsEvent('viewed-notifications') : null);

class NotificationsUtil extends React.Component {
    componentDidMount() {
        this.updateFavIcon();
    }

    componentDidUpdate(prevProps) {
        if (newNotificationsCountHasChanged(prevProps, this.props)) {
            this.updateFavIcon();
            this.setBadgeCount();
        }
    }

    // Needed to use this instead of Helmet as Helmet wouldn't update when the tab wasn't active
    updateFavIcon = () => {
        const { newNotificationsCount } = this.props;

        const favIconMetaTag = document.querySelector('link[rel="shortcut icon"]');

        if (!favIconMetaTag) return;

        favIconMetaTag.href = newNotificationsCount ? FAVICON_ON : FAVICON;
    };

    setBadgeCount = () => {
        const { newNotificationsCount } = this.props;
        navigator && navigator.setAppBadge && navigator.setAppBadge(newNotificationsCount);
    };

    render() {
        const { tooltipEnabled, newNotificationsCount, pageTitle } = this.props;

        const title = newNotificationsCount ? `(${newNotificationsCount}) ${pageTitle}` : pageTitle;

        return (
            <DocumentTitle title={title}>
                <div className="HeaderUtil NotificationsUtil">
                    <TooltipSource
                        tooltipText="Notifications"
                        distance={0}
                        enabled={tooltipEnabled}
                        delay={700}
                        triggerOnMouseEnter
                    >
                        <PopupTrigger
                            popupId={PopupIds.NOTIFICATIONS}
                            className="button-notification header-util-button"
                            closeSection={PopupSections.HEADER}
                            onToggle={dispatchNotificationsAnalyticsEvent}
                        >
                            {(popupOpen) => (
                                <div className="header-util-button-content">
                                    <Icon name="nav-notifications" />
                                    <CountIndicator count={newNotificationsCount} hide={popupOpen} badge />
                                </div>
                            )}
                        </PopupTrigger>
                        <NotificationsPopup />
                    </TooltipSource>
                    <PushNotificationsSuggestionPopup />
                </div>
            </DocumentTitle>
        );
    }
}

const enhance = compose(
    notificationsFetchDecorator,
    notificationsObservedDecorator,
    markSeenNotificationsDecorator,
    connect(notificationsUtilSelector),
);

NotificationsUtil.propTypes = {
    notifications: PropTypes.object,
    newNotificationsCount: PropTypes.number,
    notificationsReady: PropTypes.bool,
    currentUser: PropTypes.object,
    tooltipEnabled: PropTypes.bool,
    isMobileView: PropTypes.bool,
    pageTitle: PropTypes.string,
};

export default enhance(NotificationsUtil);
